import * as React from 'react';
import { sortBy } from 'lodash';
import { connect } from 'react-redux';

import {
  ModalFrame,
  ModalFrameFooter,
  ModalFrameHeader
} from 'components/modal';
import ActionButton from 'components/buttons/action-button/ActionButton';
import TabsSection from 'components/tabs/tabs-section/TabsSection';

import InspirationalMaterialsModalTab from './tab/InspirationalMaterialsModalTab';
import InspirationalMaterialsSelected from '../inspirational-materials-selected/InspirationalMaterialsSelected';
import InspirationalMaterialsUpload from '../inspirational-materials-upload/InspirationalMaterialsUpload';
import InspirationalMaterialsDiscovery from '../inspirational-materials-discovery/InspirationalMaterialsDiscovery';
import InspirationalMaterialsCampaignContent from '../inspirational-materials-campaign-content/InspirationalMaterialsCampaignContent';

import { setCampaignAssets } from 'modules/campaigns/reducers/campaignAssets';
import { removeMaterial, resetMaterials, setMaterials } from '../../reducers/inspirationalMaterials';

import { __t, applyTranslations } from 'utils/language';
import { isCcmOnlyFocusedOrganization } from 'utils/organization';

import './inspirational-materials-modal.scss';

import type { CampaignAsset, Organization } from 'types';

applyTranslations(require('./inspirational-materials-modal.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  onCloseModal: () => void
};

type StateProps = {
  existingAssets: Array<CampaignAsset>,
  newAssets: Array<CampaignAsset>,
  organization: Organization
};

type DispatchProps = {
  dispatchRemoveMaterial: (a: CampaignAsset) => void,
  dispatchSetMaterials: (a: Array<CampaignAsset>) => void,
  dispatchResetMaterials: () => void,
  dispatchSetCampaignAssets: (a: Array<CampaignAsset>) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const TAB_ID = {
  DEFAULT: 'default',
  CONTENT_DISCOVERY: 'content-discovery',
  CAMPAIGN_CONTENT: 'campaign-content'
};

const TABS_ITEMS = [
  {
    label: __t('inspirational_materials_modal.tabs.default'),
    identifier: TAB_ID.DEFAULT
  },
  {
    label: __t('inspirational_materials_modal.tabs.discovery'),
    identifier: TAB_ID.CONTENT_DISCOVERY
  },
  {
    label: __t('inspirational_materials_modal.tabs.campaign_content'),
    identifier: TAB_ID.CAMPAIGN_CONTENT
  }
];

const InspirationalMaterialsModal = (
  {
    newAssets,
    existingAssets,
    organization,
    onCloseModal,
    dispatchSetCampaignAssets,
    dispatchRemoveMaterial,
    dispatchResetMaterials,
    dispatchSetMaterials
  }: Props
): JSX.Element => {
  const [currentTab, setTab] = React.useState(TAB_ID.DEFAULT);
  const isCcmOnlyOrganization = isCcmOnlyFocusedOrganization(organization);

  const updatedTabs = TABS_ITEMS.filter(tab => (
    !(isCcmOnlyOrganization && tab.identifier === TAB_ID.CAMPAIGN_CONTENT)
  ));

  React.useEffect(() => {
    dispatchSetMaterials(sortBy(existingAssets, 'id'));
    return () => dispatchResetMaterials();
  }, []);

  return (
    <ModalFrame>
      <ModalFrameHeader title={__t('inspirational_materials_modal.title')} />

      <section
        className="inspirational-materials-modal__tabs"
      >
        <TabsSection
          items={updatedTabs}
          activeTab={currentTab}
          onClick={(tab) => setTab(tab || TAB_ID.DEFAULT)}
        />

        <InspirationalMaterialsModalTab isVisible={currentTab === TAB_ID.DEFAULT}>
          <InspirationalMaterialsUpload />
        </InspirationalMaterialsModalTab>

        <InspirationalMaterialsModalTab isVisible={currentTab === TAB_ID.CONTENT_DISCOVERY}>
          <InspirationalMaterialsDiscovery />
        </InspirationalMaterialsModalTab>

        <InspirationalMaterialsModalTab isVisible={currentTab === TAB_ID.CAMPAIGN_CONTENT}>
          <InspirationalMaterialsCampaignContent />
        </InspirationalMaterialsModalTab>

        <InspirationalMaterialsSelected
          assets={newAssets}
          onDeleteAsset={dispatchRemoveMaterial}
        />
      </section>

      <ModalFrameFooter noMargin>
        <ActionButton
          color="tertiary"
          onClick={onCloseModal}
        >
          {__t('collabary_common.action_button.cancel')}
        </ActionButton>

        <ActionButton
          color="primary"
          onClick={() => {
            dispatchSetCampaignAssets(newAssets);
            onCloseModal();
          }}
        >
          {__t('collabary_common.action_button.save')}
        </ActionButton>
      </ModalFrameFooter>
    </ModalFrame>
  );
};

const mapStateToProps = ({ campaignAssets, inspirationalMaterials, organizations }): StateProps => ({
  existingAssets: campaignAssets.assets,
  newAssets: inspirationalMaterials.assets,
  organization: organizations.currentOrganization
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchRemoveMaterial: (asset: CampaignAsset) => dispatch(removeMaterial(asset)),
  dispatchSetMaterials: (assets: Array<CampaignAsset>) => dispatch(setMaterials(assets)),
  dispatchResetMaterials: () => dispatch(resetMaterials()),
  dispatchSetCampaignAssets: (assets: Array<CampaignAsset>) => dispatch(setCampaignAssets(assets))
});

export default connect(mapStateToProps, mapDispatchToProps)(InspirationalMaterialsModal);
